import { Fragment, useEffect, useMemo, useState } from 'react';

import { Box, ListItemAvatar, ListItemText, Divider, List, ListItem, Card, Typography, Button, Avatar, styled, useTheme, Grid, Stack, CircularProgress } from '@mui/material';

import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import ArrowForwardTwoToneIcon from '@mui/icons-material/ArrowForwardTwoTone';
import InquiriesPriceCalcs from './InquiriesPriceCalcs';
import InquiriesAmount from './InquiriesAmount';
import InquiriesTotalValue from './InquiriesTotalValue';
import InquiriesStatus from './InquiriesStatus';
import { RootState } from 'src/redux/store';
import { connect, useDispatch } from 'react-redux';
import { InquiryResource, UserResource, WizardDashboardStatisticResource, WizardStatisticRestControllerService } from 'src/backend/market';
import { updatePageSize } from 'src/redux/thunks/inquiries.thunk';
import { formatDate, formatPrice, getMonthName } from 'src/utils/FormatHelpers';
import { Link } from 'react-router-dom';
import { nav } from 'src/statics/navigations';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { NUMBER_FORMAT_SETTING_NAME, CURRENCY_SETTING_NAME } from 'src/statics/statics';
import { findGeneralSetting } from 'src/utils/CalcHelpers';

const ListGridWrapper = styled(Grid)(
    ({ theme }) => `
        padding-left: 0 !important;
        border-left: 1px solid ${theme.colors.alpha.black[7]};
    `
);
const ListWrapper = styled(List)(
    ({ theme }) => `
        .MuiDivider-root:first-of-type {
            display: none;
        }
    `
);
const ChartWrapper = styled(Chart)(
    ({ theme }) => `
        svg {
            overflow: visible;
        }
    `
);
const DividerWrapper = styled(Divider)(
    ({ theme }) => `
        background-color: ${theme.colors.alpha.black[7]};
    `
);

interface Props {
    inquiries: { [key: string]: InquiryResource };
    user: UserResource;
    loading: boolean;
}

const Inquiries: React.FC<Props> = ({ inquiries, user, loading }) => {
    const { t }: { t: any } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const [statistic, setStatistic] = useState<WizardDashboardStatisticResource>({});

    useEffect(() => {
        dispatch(updatePageSize(5));

        WizardStatisticRestControllerService.getWizardDashboardStatistic(user.id).then((response) => {
            setStatistic(response);
        });
    }, []);

    const monthlyData = useMemo(() => {
        const data = _.sortBy(statistic?.monthlyWizardCount, ['year', 'month']);
        let found = false;

        const filteredData = [];
        for (const d of data) {
            if (found || d.count > 0) {
                found = true;
                filteredData.push(d);
            }
        }

        if (filteredData.length === 1) {
            const monthData = filteredData[0];
            const previousMonth = DateTime.fromObject({ year: monthData.year, month: monthData.month }).minus({ months: 1 });
            const previousMonthData = { count: 0, month: previousMonth.month, year: previousMonth.year };
            return [previousMonthData, monthData];
        }
        return filteredData;
    }, [statistic]);

    const filteredInquiries = useMemo(() => {
        return Object.values(inquiries).slice(0, 5);
    }, [inquiries]);

    const isNoData = filteredInquiries.length === 0;

    const getNumberFormat = (inquiry) => findGeneralSetting(NUMBER_FORMAT_SETTING_NAME, inquiry.generalSettings);
    const getCurrency = (inquiry) => findGeneralSetting(CURRENCY_SETTING_NAME, inquiry.generalSettings);

    const Box1Options: ApexOptions = {
        chart: {
            background: 'transparent',
            toolbar: {
                show: false
            },
            sparkline: {
                enabled: true
            },
            zoom: {
                enabled: false
            }
        },
        colors: [theme.colors.info.main],
        dataLabels: {
            enabled: false
        },
        theme: {
            mode: theme.palette.mode
        },
        stroke: {
            show: true,
            colors: [theme.colors.info.main],
            width: 3
        },
        legend: {
            show: false
        },
        fill: {
            gradient: {
                shade: 'light',
                type: 'vertical',
                shadeIntensity: 0.4,
                inverseColors: false,
                opacityFrom: 0.9,
                opacityTo: 0,
                stops: [0, 100]
            }
        },
        labels: monthlyData.map((data) => getMonthName(data.month)),
        xaxis: {
            labels: {
                show: false
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            },
            crosshairs: {
                width: 1
            }
        },
        yaxis: {
            show: false,
            min: 0
        },
        tooltip: {
            enabled: !isNoData
        }
    };
    const Box1Data = [
        {
            name: t('monthlyWizardCount'),
            data: isNoData ? [1, 1] : monthlyData.map((data) => data.count)
        }
    ];

    return (
        <Card id={'inquiriesComponentDashboard'} sx={{ display: 'flex', position: 'relative', minHeight: 388 }}>
            {loading ? (
                <Stack justifyContent="center" alignItems="center" width="100%">
                    <CircularProgress />
                </Stack>
            ) : (
                <Grid container direction="row" justifyContent="center" alignItems="stretch" spacing={2}>
                    <Grid item xs={12} lg={3.3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box pt={3} px={3}>
                            <Typography variant="h3">{t('wizardCardTitle')}</Typography>
                            <Typography variant="subtitle2">{t('wizardCardExplanation')}</Typography>
                        </Box>
                        <ChartWrapper options={Box1Options} series={Box1Data} type="area" height={270} sx={{ marginTop: 'auto' }} />
                    </Grid>
                    <ListGridWrapper item xs={12} lg={3.3}>
                        <Typography variant="subtitle2" sx={{ margin: '10px 18px', fontWeight: 'bold' }} pl={{ xs: 2, lg: 0 }}>
                            {t('latestInquires')}
                        </Typography>
                        <DividerWrapper />
                        <ListWrapper disablePadding>
                            {filteredInquiries.map((inquiry) => (
                                <Fragment key={inquiry.id}>
                                    <DividerWrapper />
                                    <ListItem sx={{ py: 1.2, px: 2.5, pl: { xs: 4, lg: 2.5 } }}>
                                        <ListItemAvatar sx={{ mr: 0, minWidth: '46px' }}>
                                            <Avatar alt={inquiry.customer.company || '-'} src="/broken-image.jpg" sx={{ minWidth: '32px', width: '32px', height: '32px' }} />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={inquiry.customer.company || inquiry.id}
                                            primaryTypographyProps={{ variant: 'subtitle2', noWrap: true, fontSize: '13px' }}
                                            secondary={formatDate(inquiry.supplyDate, 'dd.MM.yyyy')}
                                            secondaryTypographyProps={{ variant: 'subtitle2', noWrap: true, fontSize: '11px' }}
                                        />
                                        <Typography sx={{ pr: 2, opacity: 0.5 }} variant="h4">
                                            {formatPrice(inquiry.price.price, false, getNumberFormat(inquiry), getCurrency(inquiry))}
                                        </Typography>
                                        <Button
                                            component={Link}
                                            to={nav.WIZARD.sub.DETAIL.path.replace(':id', inquiry.id)}
                                            variant="text"
                                            color="secondary"
                                            sx={{
                                                padding: '5px',
                                                backgroundColor: `${theme.colors.secondary.lighter}`,
                                                '&:hover': {
                                                    backgroundColor: `${theme.colors.secondary.main}`,
                                                    color: `${theme.palette.getContrastText(theme.colors.secondary.main)}`
                                                }
                                            }}
                                        >
                                            {t('view')}
                                        </Button>
                                    </ListItem>
                                </Fragment>
                            ))}
                            {isNoData && (
                                <Typography variant="subtitle2" sx={{ py: 1.2 }} textAlign="center">
                                    {t('noDataYet')}
                                </Typography>
                            )}
                        </ListWrapper>
                        {!isNoData && (
                            <>
                                <DividerWrapper />
                                <Box p={0.5} sx={{ textAlign: 'center' }}>
                                    <Button component={Link} to={nav.CALCULATIONS.path} variant="text" endIcon={<ArrowForwardTwoToneIcon />} size="small">
                                        {t('viewAll')}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </ListGridWrapper>
                    <ListGridWrapper item xs={12} lg={5.4} sx={{ display: 'flex' }} justifyContent="center" alignItems="center">
                        <Grid container spacing={3} p={3} justifyContent="center">
                            <Grid item xs={12} lg={6}>
                                <InquiriesAmount value={statistic.inquiryCount} monthlyIncrease={isNoData ? 0 : statistic.inquiryCountMonthlyRelativeIncrease} />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InquiriesPriceCalcs value={statistic.priceCalculationsCount} monthlyIncrease={isNoData ? 0 : statistic.priceCalculationsCountMonthlyRelativeIncrease} />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InquiriesTotalValue value={statistic.inquiryTotalPrice} monthlyIncrease={isNoData ? 0 : statistic.inquiryTotalPriceMonthlyRelativeIncrease} />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <InquiriesStatus finishedWizardsPercent={Math.round(statistic.finishedWizardsPercent * 100)} isNoData={isNoData} />
                            </Grid>
                        </Grid>
                    </ListGridWrapper>
                </Grid>
            )}
        </Card>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        inquiries: state.inquiries.inquiries,
        user: state.user.currentUser,
        loading: state.inquiries.loading,
        inquiryStates: state.constants.inquiryStates,
        pageSize: state.inquiries.inquiriesTablePaging.pageSize,
        page: state.inquiries.inquiriesTablePaging.page,
        rowCount: state.inquiries.inquiriesTablePaging.rowCount
    };
};
export default connect(mapStateToProps)(Inquiries);
